import { MIN_PASSWORD_LENGTH } from '../constants/settingsPage';
import { useSnackbar } from '../components/common/Snackbar';

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid or empty (optional field)
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || phone.trim() === '') {
    return true; // Empty phone is allowed (optional field)
  }
  // Remove all spaces and dashes for validation
  const cleaned = phone.replace(/[\s-]/g, '');
  // Pattern: optional + prefix, followed by 10-15 digits
  const phoneRegex = /^(\+)?\d{10,15}$/;
  return phoneRegex.test(cleaned);
};

/**
 * Validate password change data
 * @param {Object} passwordData - Password data object
 * @param {Function} showSnackbar - Snackbar function
 * @returns {boolean} True if valid
 */
export const validatePasswordChange = (passwordData, showSnackbar) => {
  const { currentPassword, newPassword, confirmPassword } = passwordData;

  if (!currentPassword || !newPassword || !confirmPassword) {
    showSnackbar('Please fill in all password fields', 'error');
    return false;
  }

  if (newPassword.length < MIN_PASSWORD_LENGTH) {
    showSnackbar(`New password must be at least ${MIN_PASSWORD_LENGTH} characters long`, 'error');
    return false;
  }

  if (newPassword === currentPassword) {
    showSnackbar('New password must be different from current password', 'error');
    return false;
  }

  if (newPassword !== confirmPassword) {
    showSnackbar('New password and confirm password do not match', 'error');
    return false;
  }

  return true;
};

/**
 * Get user initials from name
 * @param {string} name - User's full name
 * @returns {string} Uppercase initials (max 2 characters)
 */
export const getUserInitials = (name) => {
  if (!name) return 'U';
  const initials = name
    .split(' ')
    .map((n) => n.charAt(0))
    .join('')
    .substring(0, 2);
  return (initials || 'U').toUpperCase();
};

/**
 * Extract error message from error object
 * @param {Error} error - Error object
 * @param {string} defaultMessage - Default error message
 * @returns {string} Extracted error message
 */
export const extractErrorMessage = (error, defaultMessage) => {
  if (error.response?.data) {
    const errorData = error.response.data;
    // Backend returns: { success: false, error: "...", message: "..." }
    return errorData.message || errorData.error || defaultMessage;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};
